import StageData from '@ps/store/workbench/stage/stage-data'
import { isTransformer } from '@ps/store/workbench/stage/config/layer-config'
import Konva from 'konva'
import {
  guideResult,
  itemBounds,
  lineGuideStops,
  snappingEdges,
  guideDiffResult,
  allowSnapType,
} from '@ps/types/workbench/stage.type'
import {
  guideLineOptions,
  referenceLineGroup,
} from '@ps/store/workbench/stage/config/ruler-config'
import store from '@ps/store'
import { GUIDELINE_OFFSET } from './config/snap-config'
import { Vector2d } from 'konva/lib/types'

// 以下代码参考了konva的代码：https://konvajs.org/docs/sandbox/Objects_Snapping.html
// were can we snap our objects?
export function getLineGuideStops(skipShape: allowSnapType): lineGuideStops {
  const Stage = StageData.stage!

  // 获取设备偏移值，用于计算设备宽高吸附线的坐标
  const sceneOffset: Vector2d = store.state.workspace.stage.sceneOffset
  const sceneScale: number = store.state.workspace.game.device.scale
  const sceneWidth: number = store.state.workspace.game.device.width
  const sceneHeight: number = store.state.workspace.game.device.height

  // we can snap to stage borders and the center of the stage
  const vertical: number[] = [
    sceneOffset.x,
    sceneOffset.x + sceneWidth * sceneScale,
    sceneOffset.x + (sceneWidth * sceneScale) / 2,
  ]
  const horizontal: number[] = [
    sceneOffset.y,
    sceneOffset.y + sceneHeight * sceneScale,
    sceneOffset.y + (sceneHeight * sceneScale) / 2,
  ]
  // 添加辅助线
  Stage.find(`.${referenceLineGroup.name}`).forEach((line) => {
    const tempID = ['verticalTempLine', 'horizontalTempLine']
    // 排查临时参考线
    if (tempID.includes(line.id())) {
      return
    }
    // 兼容 skipShape 为 konva.Rect 的情况
    if (line === skipShape) {
      return
    }
    const type = line.getAttr('referenceLineType')
    if (type === 'horizontal') {
      horizontal.push(line.y())
    } else if (type === 'vertical') {
      vertical.push(line.x())
    }
  })
  return {
    vertical,
    horizontal,
  }
}

// 提供公共函数，通过组的 getClientRect 和 getAbsolutePosition 获取组的边界
export function getItemBoundByBox(
  box: {
    x: number
    y: number
    width: number
    height: number
  },
  absPos: {
    x: number
    y: number
  },
): itemBounds {
  return {
    vertical: [
      {
        guide: box.x,
        offset: absPos.x - box.x,
        snap: 'start',
      },
      {
        guide: box.x + box.width / 2,
        offset: absPos.x - box.x - box.width / 2,
        snap: 'center',
      },
      {
        guide: box.x + box.width,
        offset: absPos.x - box.x - box.width,
        snap: 'end',
      },
    ],
    horizontal: [
      {
        guide: box.y,
        offset: absPos.y - box.y,
        snap: 'start',
      },
      {
        guide: box.y + box.height / 2,
        offset: absPos.y - box.y - box.height / 2,
        snap: 'center',
      },
      {
        guide: box.y + box.height,
        offset: absPos.y - box.y - box.height,
        snap: 'end',
      },
    ],
  }
}

// what points of the object will trigger to snapping?
// it can be just center of the object
export function getObjectSnappingEdges(node: allowSnapType): itemBounds {
  let box = node.getClientRect()
  const absPos = node.absolutePosition()
  // 因为 transformer 的边界存在一些 Anchors，需要增加/去掉这些补偿
  // 如果有旋转之后，这个补偿值就不准确了，所以这里不传 transformer
  if (isTransformer(node)) {
    const transformer = node as Konva.Transformer
    const offset = {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    }
    if (transformer.enabledAnchors() && transformer.enabledAnchors().length) {
      offset.x = 15
      offset.y = 15
      offset.width = -30
      offset.height = -30
      if (transformer.rotateEnabled()) {
        offset.y = 35
        offset.height = -50
      }
    } else if (transformer.rotateEnabled()) {
      offset.y = 35
      offset.height = -35
    }
    box = {
      x: box.x + offset.x,
      y: box.y + offset.y,
      width: box.width + offset.width,
      height: box.height + offset.height,
    }
  }
  return getItemBoundByBox(box, absPos)
}

// find all snapping possibilities
export function getGuides(
  lineGuideStops: lineGuideStops,
  itemBounds: itemBounds,
): guideResult[] {
  const resultV: guideDiffResult[] = []
  const resultH: guideDiffResult[] = []

  lineGuideStops.vertical.forEach((lineGuide: number) => {
    itemBounds.vertical.forEach((itemBound: snappingEdges) => {
      const diff = Math.abs(lineGuide - itemBound.guide)
      // if the distance between guild line and object snap point is close we can consider this for snapping
      if (diff < GUIDELINE_OFFSET) {
        resultV.push({
          lineGuide: lineGuide,
          diff: diff,
          snap: itemBound.snap,
          offset: itemBound.offset,
        })
      }
    })
  })

  lineGuideStops.horizontal.forEach((lineGuide: number) => {
    itemBounds.horizontal.forEach((itemBound: snappingEdges) => {
      const diff = Math.abs(lineGuide - itemBound.guide)
      if (diff < GUIDELINE_OFFSET) {
        resultH.push({
          lineGuide: lineGuide,
          diff: diff,
          snap: itemBound.snap,
          offset: itemBound.offset,
        })
      }
    })
  })

  const guides = []

  // find the closest snap
  const minV = resultV.sort(
    (a: guideDiffResult, b: guideDiffResult) => a.diff - b.diff,
  )[0]
  const minH = resultH.sort(
    (a: guideDiffResult, b: guideDiffResult) => a.diff - b.diff,
  )[0]
  if (minV) {
    guides.push({
      lineGuide: minV.lineGuide,
      offset: minV.offset,
      orientation: 'V',
      snap: minV.snap,
    })
  }
  if (minH) {
    guides.push({
      lineGuide: minH.lineGuide,
      offset: minH.offset,
      orientation: 'H',
      snap: minH.snap,
    })
  }
  return guides
}

export function drawGuides(guides: guideResult[]): void {
  if (!StageData.layer) {
    console.warn('缺乏 layer')
    return
  }
  const layer = StageData.layer
  guides.forEach((lg) => {
    if (lg.orientation === 'H') {
      const line = new Konva.Line({
        points: [-6000, 0, 6000, 0],
        ...guideLineOptions,
      })
      layer.add(line)
      line.absolutePosition({
        x: 0,
        y: lg.lineGuide,
      })
    } else if (lg.orientation === 'V') {
      const line = new Konva.Line({
        points: [0, -6000, 0, 6000],
        ...guideLineOptions,
      })
      layer.add(line)
      line.absolutePosition({
        x: lg.lineGuide,
        y: 0,
      })
    }
  })
}

export function removeGuides(): void {
  if (!StageData.layer) {
    console.warn('缺乏 layer')
    return
  }
  const layer = StageData.layer
  layer.find(`.${guideLineOptions.name}`).forEach((l) => l.destroy())
}

/**
 * 获取组元素的边界
 * @param group 组元素集合
 */
export function getGroupSnappingEdges(group: allowSnapType[]): itemBounds {
  const box = {
    x: Infinity,
    y: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
    width: 0,
    height: 0,
  }
  group.forEach((shape) => {
    const itemBox = shape.getClientRect()
    // 获取最小的 x/y 作为组合的边界
    if (itemBox.x < box.x) {
      box.x = itemBox.x
    }
    if (itemBox.y < box.y) {
      box.y = itemBox.y
    }
    if (itemBox.x + itemBox.width > box.maxX) {
      box.maxX = itemBox.x + itemBox.width
    }
    if (itemBox.y + itemBox.height > box.maxY) {
      box.maxY = itemBox.y + itemBox.height
    }
  })
  return getItemBoundByBox(
    {
      x: box.x,
      y: box.y,
      width: box.maxX - box.x,
      height: box.maxY - box.y,
    },
    {
      x: box.x,
      y: box.y,
    },
  )
}
